import React, { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  Folder, 
  FileText, 
  User as UserIcon, 
  Menu, 
  X, 
  ArrowLeft, 
  LogOut 
} from 'lucide-react';
import { useSimpleAuth } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { Tables } from '../types/supabase';
import type { User } from '@supabase/supabase-js';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { signOut, user, isAdmin } = useSimpleAuth() as { signOut: () => Promise<void>; user: User | null; isAdmin: boolean; };
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);


  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const [adminProfile, setAdminProfile] = useState<Tables<'admin_profiles'> | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch admin profile
  useEffect(() => {
    const fetchAdminProfile = async () => {
      if (user && isAdmin) {
        const { data } = await supabase
          .from('admin_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setAdminProfile(data);
        }
      }
    };

    fetchAdminProfile();
  }, [user, isAdmin]);

  const navItems = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/projects', label: 'Projects', icon: Folder },
    { to: '/admin/articles', label: 'Articles', icon: FileText },
    { to: '/admin/profile', label: 'Profile', icon: UserIcon }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-400/10 to-pink-400/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-green-300/10 to-blue-300/10 rounded-full blur-3xl"
        />
      </div>

      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg border border-gray-200/50 dark:border-gray-700/50"
        >
{isSidebarOpen ? <X size={24} className="text-gray-700 dark:text-gray-300" /> : <Menu size={24} className="text-gray-700 dark:text-gray-300" />}
        </button>
      )}

      {/* Sidebar */}
      <AnimatePresence>
        {(isSidebarOpen || !isMobile) && (
          <motion.nav
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", damping: 20 }}
            className={`fixed top-0 left-0 h-screen w-72 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50 shadow-2xl z-40 ${
              isMobile ? 'shadow-2xl' : ''
            }`}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center gap-4">
                {adminProfile?.avatar_url ? (
                  <img
                    src={adminProfile.avatar_url}
                    alt="Admin"
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white font-semibold">
                    {adminProfile?.display_name?.[0] || user?.email?.[0] || 'A'}
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    Admin Panel
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {adminProfile?.display_name || user?.email || 'Administrator'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Navigation */}
            <div className="p-4 space-y-2">
              {navItems.map(({ to, label, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => isMobile && setIsSidebarOpen(false)}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg scale-[1.02]'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 hover:scale-[1.01]'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
{React.createElement(icon, {
                        size: 22,
                        className: isActive ? 'drop-shadow-md' : 'group-hover:scale-110 transition-transform'
                      })}
                      <span className="font-medium">{label}</span>
                      {/* Active indicator */}
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className="ml-auto"
                        >
                          <div className="w-2 h-2 rounded-full bg-white shadow-glow" />
                        </motion.div>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
          
              {/* Separator */}
              <div className="my-4 h-px bg-gray-200/50 dark:bg-gray-700/50" />
              
              {/* Back to Home Link */}
              <Link
                to="/"
                className="group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-gray-700 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 hover:scale-[1.01] w-full text-left"
              >
<ArrowLeft size={22} className="group-hover:scale-110 transition-transform" />
                <span className="font-medium">Back to Home</span>
              </Link>
            </div>
            
            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/50">
              <button
                onClick={handleSignOut}
                className="group flex items-center gap-3 w-full px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-xl transition-all duration-200"
              >
<LogOut size={20} className="group-hover:scale-110 transition-transform" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30"
        />
      )}

      {/* Main Content */}
      <main className={`transition-all duration-300 ${
        isSidebarOpen && !isMobile ? 'ml-72' : 'ml-0'
      } ${isMobile ? 'pt-16' : ''}`}>
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10"
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
}